import { useEffect, useState } from 'react';
import { CalendarDays, Home, Images, MessageSquare } from 'lucide-react';
import { conversationsApi, familyConversationsApi } from '../api/conversations';
import { useLiveMessages } from '../hooks/useLiveMessages';
import type { Me } from '../api/auth';
import { Lockup } from '../brand/Lockup';
import { AccountMenu } from './AccountMenu';
import { HeaderButton } from './HeaderButton';

/**
 * The signed-in chrome on a main page: where you are going, and who you are.
 *
 * <b>Three destinations and a menu</b> — TROOP-120. The bar had grown a button per feature and
 * needed 551px at a 390px viewport, so it ran off the side of every phone the product is actually
 * used on; a volunteer opens this in a church basement, not on a laptop. Calendar, Conversations
 * and Photos stay as icons because they are what somebody opens the app to do. Everything else —
 * the unit switcher, the family doors, Manage, Your settings, Sign out — lives behind
 * {@link AccountMenu} on the right, which now needs about 230px.
 *
 * <b>The three icons follow the door you have.</b> For a guardian holding no membership they point
 * at their <c>/family</c> equivalents rather than disappearing: rule 13b says <c>/family</c> is a
 * front door and not a read-only annex, so a parent gets the same shape as a member and not a
 * poorer one. The first one is <i>named</i> for where it lands rather than keeping a member's word
 * for it — their home opens on their children's calendar but is headed "Your family", and a control
 * that says "Calendar" and arrives somewhere called something else is a name that did not survive
 * the trip.
 *
 * <b>Detail pages use `AppBar` instead</b>, which carries the back button and the object's name —
 * and, since TROOP-122, the same {@link AccountMenu} in the same place. That is the whole of the
 * difference between the two headers.
 */
export function AppHeader({
  me,
  currentUnitId,
  onNavigate,
}: {
  me: Me;
  currentUnitId?: string;
  onNavigate: (path: string) => void;
}) {
  const [unreadHere, setUnreadHere] = useState(0);
  const [unreadFamily, setUnreadFamily] = useState(0);
  const live = useLiveMessages();

  const current = me.memberships.find((m) => m.unitId === currentUnitId) ?? me.memberships[0];

  // A guardian holding no membership gets the same three icons pointed at the door they have.
  const home = current
    ? { path: `/units/${current.unitId}`, label: 'Calendar', Icon: CalendarDays }
    : { path: '/family', label: 'Your family', Icon: Home };

  const conversationsPath = current
    ? `/units/${current.unitId}/conversations`
    : '/family/conversations';
  const photosPath = current ? `/units/${current.unitId}/photos` : '/family/photos';

  // What the Conversations icon counts depends on which door it opens, so the badge follows it.
  const conversationsBadge = current ? unreadHere : unreadFamily;

  // The badge over the whole unit, from the endpoint that counts every thread rather than a page
  // of them. A number summed from a list would fall when somebody else posted, which reads as a
  // number rather than as a bug (TROOP-63).
  useEffect(() => {
    if (!current) {
      setUnreadHere(0);
      return;
    }

    let alive = true;

    void conversationsApi
      .unreadTotal(current.unitId)
      .then((summary) => {
        if (alive) setUnreadHere(summary.total);
      })
      // Silent: chrome that cannot count is chrome without a badge, not an error banner over
      // somebody's calendar.
      .catch(() => undefined);

    return () => {
      alive = false;
    };
    // `tick` is the point: a message landing anywhere this person can read re-counts the badge,
    // from whatever screen they are on. That is what a single always-on connection buys, and what
    // a per-conversation subscription could never have delivered -- it can only tell you about a
    // thread you already have open, which is the one place you do not need a badge.
  }, [current, live.tick]);

  // Only for a guardian who holds no membership, whose Conversations icon IS the family door.
  // Where they also hold one, that door is inside AccountMenu and the count is fetched there --
  // so exactly one of the two components ever asks for this number.
  useEffect(() => {
    if (!me.isGuardian || current) {
      setUnreadFamily(0);
      return;
    }

    let alive = true;

    void familyConversationsApi
      .unreadTotal()
      .then((summary) => {
        if (alive) setUnreadFamily(summary.total);
      })
      .catch(() => undefined);

    return () => {
      alive = false;
    };
  }, [me.isGuardian, current, live.tick]);

  return (
    <header style={{ background: 'var(--color-surface-inverse)' }}>
      <div
        className="mx-auto flex items-center justify-between gap-3"
        style={{
          maxWidth: '46rem',
          padding: '13px var(--gutter)',
          color: 'var(--color-text-on-inverse)',
        }}
      >
        <button
          type="button"
          onClick={() => onNavigate('/')}
          aria-label="TroopHerald home"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit' }}
        >
          <Lockup size={26} monochrome />
        </button>

        <div className="flex items-center gap-1 min-w-0">
          {/* The three things somebody opens the app to do. Everything else is behind the
              account control, which is what got this bar back inside a phone. */}
          <HeaderButton label={home.label} onClick={() => onNavigate(home.path)}>
            <home.Icon size={18} aria-hidden="true" />
          </HeaderButton>

          <HeaderButton
            label="Conversations"
            onClick={() => onNavigate(conversationsPath)}
            badge={conversationsBadge}
          >
            <MessageSquare size={18} aria-hidden="true" />
          </HeaderButton>

          <HeaderButton label="Photos" onClick={() => onNavigate(photosPath)}>
            <Images size={18} aria-hidden="true" />
          </HeaderButton>

          <AccountMenu me={me} currentUnitId={currentUnitId} onNavigate={onNavigate} />
        </div>
      </div>
    </header>
  );
}
